<template>
    <div>
        <form @submit.prevent="submit">
            <div class="mb-6">
                <input-text
                    v-model:content="form.name"
                    :error="errors.name"
                    :label="_mft('crm:contact.fields.name')"
                    placeholder=""
                    required="required"
                    name="name"
                    autofocus
                />

                <input-email
                    v-model:content="form.email"
                    :error="errors.email"
                    :label="_mft('crm:contact.fields.email')"
                    name="email"
                    required
                    @isEmailValid="validateEmail"
                />

                <input-text
                    v-model:content="form.title"
                    :error="errors.title"
                    :label="_mft('crm:contact.fields.title')"
                    name="title"
                />

                <input-text
                    v-model:content="form.phone"
                    :error="errors.phone"
                    :label="_mft('crm:contact.fields.phone')"
                    name="phone"
                />

                <input-text
                    v-model:content="form.linkedin"
                    :error="errors.linkedin"
                    :label="_mft('crm:contact.fields.linkedIn')"
                    name="linkedin"
                />
            </div>
            <div class="mb-6">
                <h5 class="mb-1">{{ _mft('crm:create.contact.company') }}</h5>
                <div
                    v-if="hasLeadCompany"
                    class="rounded-md border border-gray-200 bg-gray-50 px-4 py-3"
                >
                    <p class="mb-1 text-sm font-medium text-gray-900">{{ form.company_name }}</p>
                    <p
                        v-if="form.org_nr"
                        class="mb-1 text-xs text-gray-500"
                    >
                        {{ _mft('crm:contact.company.fields.corporateIdentityNumber') }}: {{ form.org_nr }}
                    </p>
                    <p
                        v-if="form.invoice_email"
                        class="mb-0 text-xs text-gray-500"
                    >
                        {{ _mft('crm:contact.company.fields.invoiceEmail') }}: {{ form.invoice_email }}
                    </p>
                </div>

                <div v-else>
                    <input-label
                        class="mb-1"
                        :label="_mft('crm:leadCompany.existingLeadCompany')"
                    />

                    <v-select
                        :map-keydown="keepEscapeInsideOpenSelect"
                        :options="existingLeadCompanies"
                        :reduce="(option) => option.id"
                        label="label"
                        v-model="form.lead_company_id"
                        :filterable="false"
                        @search="searchExistingLeadCompanies"
                    >
                        <template #search="{attributes, events}">
                            <input
                                class="vs__search"
                                :placeholder="_mft('crm:leadCompany.searchByCompanyNameOrgNumberOrInvoiceEmail')"
                                v-bind="attributes"
                                v-on="events"
                            />
                        </template>
                    </v-select>

                    <input-error
                        v-if="errors.lead_company_id"
                        :error="errors.lead_company_id"
                    />
                </div>

                <input-select
                    v-if="reference.id"
                    v-model:content="form.reference_id"
                    :options="[{value: reference.id, label: reference.name}]"
                    :error="errors.reference"
                    :label="_mft('crm:contact.fields.reference')"
                    required="required"
                    name="reference"
                />
            </div>

            <div
                v-if="matchedArchived"
                class="p-4 mb-4 text-sm border border-amber-200 bg-amber-50 rounded-card text-amber-900"
            >
                <p class="mb-1 font-semibold">{{ _mft('crm:archive.matchedArchived.title') }}</p>
                <p class="mb-3">{{ _mft('crm:archive.matchedArchived.body') }}</p>
                <div class="flex items-center space-x-3">
                    <a
                        :href="matchedArchived.lead_url"
                        class="mf-link"
                    >
                        {{ _mft('crm:archive.matchedArchived.open') }}
                    </a>
                    <button-secondary
                        v-if="matchedArchived.can_restore"
                        size="sm"
                        type="button"
                        :disabled="isRestoring"
                        @click="restoreMatchedArchived"
                    >
                        {{ _mft('crm:archive.matchedArchived.restore') }}
                    </button-secondary>
                </div>
            </div>

            <input-button
                :disabled="!emailIsValid"
                :label="submitLabel"
                :busy="isSubmitting"
                type="submit"
                class="float-right"
            />
        </form>
    </div>
</template>
<script>
import debounce from 'lodash/debounce';
import vSelect from 'vue-select';
import { keepEscapeInsideOpenSelect } from '@/utils/vueSelect.js';

export default {
    components: {
        vSelect,
    },
    emits: ['created'],
    props: {
        reference: {
            type: Object,
            default: () => ({}),
        },
        leadCompany: {
            type: Object,
            default: null,
        },
        redirectOnSuccess: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            emailIsValid: false,
            existingLeadCompanies: [],
            form: {
                name: '',
                email: '',
                title: '',
                phone: '',
                linkedin: '',
                company_name: '',
                org_nr: '',
                invoice_email: '',
                reference_id: '',
                lead_company_id: '',
            },
            errors: {
                name: '',
                email: '',
                title: '',
                phone: '',
                linkedin: '',
                reference: '',
                lead_company_id: '',
                company_name: '',
                org_nr: '',
                invoice_email: '',
            },
            isSubmitting: false,
            matchedArchived: null,
            isRestoring: false,
            submitLabel: _mft('shared:add'),
            leadCompanySearchRequestId: 0,
        };
    },
    created() {
        this.debouncedSearchExistingLeadCompanies = debounce(async (search, loading, requestId) => {
            try {
                await this.loadExistingLeadCompanies(search, requestId);
            } finally {
                if (requestId === this.leadCompanySearchRequestId) {
                    loading(false);
                }
            }
        }, 300);
    },
    beforeUnmount() {
        this.debouncedSearchExistingLeadCompanies?.cancel();
    },
    computed: {
        hasLeadCompany() {
            return Boolean(this.leadCompany && this.leadCompany.id);
        },
    },
    watch: {
        reference: {
            immediate: true,
            handler(reference) {
                this.form.reference_id = reference?.id ?? '';
            },
        },
        leadCompany: {
            immediate: true,
            handler(leadCompany) {
                this.applyLeadCompany(leadCompany);
            },
        },
    },
    mounted() {
        if (!this.hasLeadCompany) {
            const requestId = ++this.leadCompanySearchRequestId;
            this.loadExistingLeadCompanies('', requestId);
        }
    },
    methods: {
        keepEscapeInsideOpenSelect,
        async submit() {
            if (this.isSubmitting === true) {
                return false;
            }

            this.isSubmitting = true;

            const previousLabel = this.submitLabel;
            this.submitLabel = _mft('shared:save.saving');
            this.resetErrors();
            this.matchedArchived = null;

            const formData = new FormData();
            for (const key in this.form) {
                formData.append(key, this.form[key]);
            }

            try {
                const response = await axios.post(route('crm.leads.add'), formData);

                if (response.status === 200) {
                    this.$emit('created', response.data);

                    if (response.data.redirect && this.redirectOnSuccess) {
                        window.location = response.data.redirect;
                    }
                }
            } catch (error) {
                if (error.response?.status === 409 && error.response.data?.matched_archived) {
                    this.matchedArchived = error.response.data;

                    return;
                }

                const validationErrors = error.response?.data?.errors ?? {};

                Object.keys(this.errors).forEach((key) => {
                    this.errors[key] = validationErrors[key]?.[0] ?? '';
                });
            } finally {
                this.isSubmitting = false;
                this.submitLabel = previousLabel;
            }
        },
        async restoreMatchedArchived() {
            if (this.isRestoring || this.matchedArchived === null) {
                return;
            }

            this.isRestoring = true;

            try {
                await axios.post(this.matchedArchived.restore_url, {
                    lead_ids: [this.matchedArchived.lead_id],
                });

                window.location = this.matchedArchived.lead_url;
            } finally {
                this.isRestoring = false;
            }
        },
        validateEmail(valid) {
            this.emailIsValid = valid;
        },
        async loadExistingLeadCompanies(search = '', requestId = null) {
            const activeRequestId = requestId ?? ++this.leadCompanySearchRequestId;
            const response = await axios.get(route('bo.api.v1.crm.lead-company.index'), {
                params: {search},
            });

            if (activeRequestId !== this.leadCompanySearchRequestId) {
                return;
            }

            this.existingLeadCompanies = (response.data.lead_companies ?? []).map((leadCompany) => ({
                ...leadCompany,
                label: [
                    leadCompany.name,
                    leadCompany.org_nr,
                    leadCompany.invoice_email,
                ].filter(Boolean).join(' • '),
            }));
        },
        async searchExistingLeadCompanies(search, loading) {
            const requestId = ++this.leadCompanySearchRequestId;
            loading(true);
            this.debouncedSearchExistingLeadCompanies(search, loading, requestId);
        },
        applyLeadCompany(leadCompany) {
            this.form.lead_company_id = leadCompany?.id ?? '';
            this.form.company_name = leadCompany?.name ?? '';
            this.form.org_nr = leadCompany?.org_nr ?? '';
            this.form.invoice_email = leadCompany?.invoice_email ?? '';
        },
        resetErrors() {
            Object.keys(this.errors).forEach((key) => {
                this.errors[key] = '';
            });
        },
    },
};
</script>
